/**
 * Relative datetime expressions for date_range filter defaults.
 *
 * Syntax: `now` | `now[+-]N<unit>` where unit is one of
 * `s` / `mi` / `h` / `d` / `w` / `m` / `y` — second, minute, hour, day,
 * week, month, year. Case-sensitive.
 *
 * Examples: `now`, `now-7d`, `now+1h`, `now-30mi`, `now-1m`, `now-1y`.
 *
 * Mirrors the backend parser in
 * src/app/application/services/custom_dashboard.py — keep the two in sync.
 */

import { addDays, addHours, addMinutes, addMonths, addSeconds, addWeeks, addYears, format } from 'date-fns';

const RELATIVE_PATTERN = /^now(?:([+-])(\d+)(mi|[smhdwmy]))?$/;

type Unit = 's' | 'mi' | 'h' | 'd' | 'w' | 'm' | 'y';

const APPLIERS: Record<Unit, (d: Date, n: number) => Date> = {
  s: addSeconds,
  mi: addMinutes,
  h: addHours,
  d: addDays,
  w: addWeeks,
  m: addMonths,
  y: addYears,
};

export function isRelativeDatetime(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('now');
}

export function parseRelativeDatetime(expr: string, now: Date = new Date()): Date | null {
  const match = expr.trim().match(RELATIVE_PATTERN);
  if (!match) return null;
  const [, sign, amountStr, unit] = match;
  if (!sign) return now;
  const n = parseInt(amountStr, 10) * (sign === '+' ? 1 : -1);
  return APPLIERS[unit as Unit](now, n);
}

/**
 * Resolve relative `from`/`to` strings to concrete `yyyy-MM-dd`.
 * Invalid `now…` expressions and non-string values are dropped from the
 * output so callers can safely feed the result straight into a date picker.
 * Absolute strings are preserved verbatim (after trim) — callers are
 * responsible for validating their ISO format.
 */
export function resolveDateRangeDefault(
  value: { from?: string; to?: string } | null | undefined,
  now: Date = new Date(),
): { from?: string; to?: string } {
  if (!value || typeof value !== 'object') return {};
  const out: { from?: string; to?: string } = {};
  for (const key of ['from', 'to'] as const) {
    const raw = value[key];
    if (typeof raw !== 'string') continue;
    const normalized = raw.trim();
    if (!normalized) continue;
    if (isRelativeDatetime(normalized)) {
      const resolved = parseRelativeDatetime(normalized, now);
      if (resolved) out[key] = format(resolved, 'yyyy-MM-dd');
      // unparseable "now…" → drop the key so caller can fall back cleanly
    } else {
      out[key] = normalized;
    }
  }
  return out;
}
